import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { Gift, HeartHandshake, Info, Target, TrendingUp, Users } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { ModulePageTabs, ModuleTabContent, ChildTabPlaceholder } from './moduleTabs';
import TableControls from './TableControls';
import { StandardActionsCell, StandardKpiCard, StandardRecordSheetModal } from './StandardUI';
import CRMGlossary from './CRMGlossary';
import api from './api';
import { isLegacyBuCode, translateDas } from './strategicMapping';
import FunctionResourcesOverview from './FunctionResourcesOverview';
import { FunctionArchitectureOverview, FunctionProcessOverview } from './FunctionStructuralViews';
import FunctionAssistant from './FunctionAssistant';

const dictionaries = {
  FR: {
    overview: "Vue d'ensemble",
    prospects: 'Prospects',
    clients: 'Clients',
    ventes: 'Ventes',
    dons: 'Dons',
    beneficiaires: 'Bénéficiaires',
    socialFlows: 'Flux sociaux reclassés',
    inKindDonations: 'Dons en nature rapprochés',
    totalSocial: 'Total social reclassé',
    validatedSources: 'Sources validées',
    toBuild: 'À construire',
    toBuildText: 'Aucune table métier validée pour ce registre. Les anciennes données Genspark restent une référence UX, mais ne sont pas affichées comme données réelles.',
    sourceNotice: 'Données réelles dérivées de Finances > Social - Flux reclassés et des candidats dons en nature de Stocks & Actifs. Les données Genspark sont conservées uniquement comme référence de comparaison.',
    financeSocialSource: 'Finance Social',
    inventorySource: 'Stocks & Actifs',
    beneficiariesByType: "Bénéficiaires par type d'aide",
    donationsByNature: 'Dons en nature par catégorie',
    socialByYear: 'Flux sociaux par année',
    noDonationRows: 'Aucun don en nature rapproché depuis Stocks & Actifs pour le moment.',
    noBeneficiaryRows: 'Aucun flux social reclassé chargé pour le moment.',
    add: 'Ajouter',
    addTitle: 'Préparer un ajout',
    addHelp: "L'ajout est prêt côté interface. La sauvegarde sera activée après validation du schéma BigQuery CRM.",
    close: 'Fermer',
    selectedRegister: 'Registre sélectionné',
    actions: 'Actions',
    view: 'Voir',
    edit: 'Modifier',
    recordSheet: 'Fiche',
    editPending: 'La modification sera activée après validation du schéma BigQuery CRM.',
    date: 'Date',
    ref: 'Réf.',
    beneficiary: 'Bénéficiaire',
    designation: 'Désignation',
    aidType: "Type d'aide",
    amountChf: 'Montant CHF',
    amountCfa: 'Montant CFA',
    source: 'Source',
    agent: 'Agent',
    team: 'Team',
    department: 'Département',
    phase: 'Phase',
    fxRate: 'Taux FX',
    country: 'Pays',
    donor: 'Donateur',
    nature: 'Nature',
    destination: 'Destination',
    quantity: 'Quantité',
    unit: 'Unité',
    status: 'Statut',
    comment: 'Commentaire',
    readOnly: 'Registre dérivé en lecture seule. La table CRM définitive sera créée après validation du schéma métier.',
    nextSubLot: 'Prochain sous-lot',
    readOnlyPilot: 'Lecture seule',
    noConsolidatedData: 'Aucune donnée commerciale consolidée n’est encore disponible. Cette vue prépare le cadre métier sans créer de faux enregistrements.',
    prospectsPurpose: 'Qualifier les organisations, personnes et partenaires pouvant entrer dans une relation commerciale ou de coopération.',
    clientsPurpose: 'Structurer les relations commerciales actives sans les confondre avec les bénéficiaires sociaux.',
    salesPurpose: 'Suivre une opportunité commerciale jusqu’à la décision, sans ouvrir la facturation ni la comptabilité.',
    prospectsFields: ['Organisation ou personne', 'Segment et besoin', 'Source et offre pressentie', 'Responsable, action et échéance'],
    clientsFields: ['Identité et segment', 'Offre ou pack', 'Responsable et historique', 'Statut et prochaine action'],
    salesFields: ['Prospect ou client', 'Offre et montants CFA / CHF', 'Phase et probabilité', 'Responsable, action et échéance'],
    commercialBoundary: 'CRM suit la relation et l’opportunité. Finance suit la facturation, Production l’exécution et GED les pièces liées.',
    writesUnavailable: 'Ajouts et modifications seront activés plus tard avec schéma validé, droits, confirmations et traçabilité.',
    sourceScope: 'Périmètre : extraits chargés, jusqu’à 300 flux sociaux et 500 éléments de stock. Ces valeurs ne constituent pas des totaux globaux.',
    sourceAvailability: 'Disponibilité des sources',
    sourcesAvailable: 'sources accessibles',
    available: 'Disponible',
    unavailable: 'Indisponible',
    loadingLabel: 'Chargement',
    loadedExtract: 'Extrait chargé',
    readAt: 'Lu le',
    glossary: 'Glossaire'
  },
  EN: {
    overview: 'Overview',
    prospects: 'Prospects',
    clients: 'Clients',
    ventes: 'Sales',
    dons: 'Donations',
    beneficiaires: 'Beneficiaries',
    socialFlows: 'Reclassified social flows',
    inKindDonations: 'Matched in-kind donations',
    totalSocial: 'Total reclassified social',
    validatedSources: 'Validated sources',
    toBuild: 'To build',
    toBuildText: 'No validated business table exists for this register. Previous Genspark data remains a UX reference, but is not shown as real data.',
    sourceNotice: 'Real data derived from Finance > Social - Reclassified flows and in-kind donation candidates from Stocks & Assets. Genspark data is kept only as comparison reference.',
    financeSocialSource: 'Finance Social',
    inventorySource: 'Stocks & Assets',
    beneficiariesByType: 'Beneficiaries by aid type',
    donationsByNature: 'In-kind donations by category',
    socialByYear: 'Social flows by year',
    noDonationRows: 'No in-kind donation matched from Stocks & Assets yet.',
    noBeneficiaryRows: 'No reclassified social flow loaded yet.',
    add: 'Add',
    addTitle: 'Prepare a new record',
    addHelp: 'The entry point is ready in the interface. Saving will be enabled after the BigQuery CRM schema is validated.',
    close: 'Close',
    selectedRegister: 'Selected register',
    actions: 'Actions',
    view: 'View',
    edit: 'Edit',
    recordSheet: 'Record sheet',
    editPending: 'Editing will be enabled after the BigQuery CRM schema is validated.',
    date: 'Date',
    ref: 'Ref.',
    beneficiary: 'Beneficiary',
    designation: 'Description',
    aidType: 'Aid type',
    amountChf: 'Amount CHF',
    amountCfa: 'Amount CFA',
    source: 'Source',
    agent: 'Agent',
    team: 'Team',
    department: 'Department',
    phase: 'Phase',
    fxRate: 'FX rate',
    country: 'Country',
    donor: 'Donor',
    nature: 'Nature',
    destination: 'Destination',
    quantity: 'Quantity',
    unit: 'Unit',
    status: 'Status',
    comment: 'Comment',
    readOnly: 'Derived read-only register. The definitive CRM table will be created after the business schema is validated.',
    nextSubLot: 'Next sub-lot',
    readOnlyPilot: 'Read only',
    noConsolidatedData: 'No consolidated commercial data is available yet. This view prepares the business scope without creating fake records.',
    prospectsPurpose: 'Qualify organizations, people and partners that may enter a commercial or cooperation relationship.',
    clientsPurpose: 'Structure active commercial relationships without confusing them with social beneficiaries.',
    salesPurpose: 'Track a commercial opportunity through decision without opening invoicing or accounting.',
    prospectsFields: ['Organization or person', 'Segment and need', 'Source and expected offer', 'Owner, action and deadline'],
    clientsFields: ['Identity and segment', 'Offer or pack', 'Owner and history', 'Status and next action'],
    salesFields: ['Prospect or client', 'Offer and CFA / CHF amounts', 'Phase and probability', 'Owner, action and deadline'],
    commercialBoundary: 'CRM tracks the relationship and opportunity. Finance handles invoicing, Production handles delivery and GED keeps related documents.',
    writesUnavailable: 'Create and edit actions will return later with a validated schema, permissions, confirmations and traceability.',
    sourceScope: 'Scope: loaded extracts, up to 300 social flows and 500 stock items. These values are not global totals.',
    sourceAvailability: 'Source availability',
    sourcesAvailable: 'sources available',
    available: 'Available',
    unavailable: 'Unavailable',
    loadingLabel: 'Loading',
    loadedExtract: 'Loaded extract',
    readAt: 'Read on',
    glossary: 'Glossary'
  },
  DE: {
    overview: 'Übersicht',
    prospects: 'Interessenten',
    clients: 'Kunden',
    ventes: 'Verkäufe',
    dons: 'Spenden',
    beneficiaires: 'Begünstigte',
    socialFlows: 'Umklassierte Sozialflüsse',
    inKindDonations: 'Abgeglichene Sachspenden',
    totalSocial: 'Total umklassiert sozial',
    validatedSources: 'Validierte Quellen',
    toBuild: 'Im Aufbau',
    toBuildText: 'Für dieses Register gibt es noch keine validierte Fachtabelle. Frühere Genspark-Daten bleiben eine UX-Referenz, werden aber nicht als echte Daten angezeigt.',
    sourceNotice: 'Echte Daten aus Finanzen > Sozial - umklassierte Flüsse und Sachspenden-Kandidaten aus Lager & Aktiven. Genspark-Daten bleiben nur Vergleichsreferenz.',
    financeSocialSource: 'Finance Social',
    inventorySource: 'Lager & Aktiven',
    beneficiariesByType: 'Begünstigte nach Hilfetyp',
    donationsByNature: 'Sachspenden nach Kategorie',
    socialByYear: 'Sozialflüsse nach Jahr',
    noDonationRows: 'Noch keine Sachspende aus Lager & Aktiven abgeglichen.',
    noBeneficiaryRows: 'Noch kein umklassierter Sozialfluss geladen.',
    add: 'Hinzufügen',
    addTitle: 'Neuen Eintrag vorbereiten',
    addHelp: 'Der Einstieg ist in der Oberfläche vorbereitet. Das Speichern wird nach Validierung des BigQuery-CRM-Schemas aktiviert.',
    close: 'Schliessen',
    selectedRegister: 'Ausgewähltes Register',
    actions: 'Aktionen',
    view: 'Ansehen',
    edit: 'Bearbeiten',
    recordSheet: 'Datensatz',
    editPending: 'Die Bearbeitung wird nach Validierung des BigQuery-CRM-Schemas aktiviert.',
    date: 'Datum',
    ref: 'Ref.',
    beneficiary: 'Begünstigter',
    designation: 'Bezeichnung',
    aidType: 'Hilfetyp',
    amountChf: 'Betrag CHF',
    amountCfa: 'Betrag CFA',
    source: 'Quelle',
    agent: 'Agent',
    team: 'Team',
    department: 'Abteilung',
    phase: 'Phase',
    fxRate: 'FX-Kurs',
    country: 'Land',
    donor: 'Spender',
    nature: 'Art',
    destination: 'Ziel',
    quantity: 'Menge',
    unit: 'Einheit',
    status: 'Status',
    comment: 'Kommentar',
    readOnly: 'Abgeleitetes Leseregister. Die definitive CRM-Tabelle wird nach Validierung des Fachschemas erstellt.',
    nextSubLot: 'Nächstes Teilpaket',
    readOnlyPilot: 'Nur lesen',
    noConsolidatedData: 'Es sind noch keine konsolidierten Vertriebsdaten verfügbar. Diese Ansicht bereitet den Fachrahmen vor, ohne fiktive Einträge anzulegen.',
    prospectsPurpose: 'Organisationen, Personen und Partner für eine mögliche Geschäfts- oder Kooperationsbeziehung qualifizieren.',
    clientsPurpose: 'Aktive Geschäftsbeziehungen strukturieren, ohne sie mit sozialen Begünstigten zu verwechseln.',
    salesPurpose: 'Eine Verkaufschance bis zur Entscheidung verfolgen, ohne Fakturierung oder Buchhaltung zu öffnen.',
    prospectsFields: ['Organisation oder Person', 'Segment und Bedarf', 'Quelle und vorgesehene Offerte', 'Verantwortung, Aktion und Termin'],
    clientsFields: ['Identität und Segment', 'Offerte oder Paket', 'Verantwortung und Verlauf', 'Status und nächste Aktion'],
    salesFields: ['Interessent oder Kunde', 'Offerte und Beträge CFA / CHF', 'Phase und Wahrscheinlichkeit', 'Verantwortung, Aktion und Termin'],
    commercialBoundary: 'CRM verfolgt Beziehung und Verkaufschance. Finanzen bearbeitet Rechnungen, Produktion die Ausführung und GED die zugehörigen Dokumente.',
    writesUnavailable: 'Erstellen und Bearbeiten werden später mit validiertem Schema, Rechten, Bestätigungen und Nachverfolgung aktiviert.',
    sourceScope: 'Umfang: geladene Auszüge mit bis zu 300 Sozialflüssen und 500 Bestandselementen. Diese Werte sind keine Gesamtsummen.',
    sourceAvailability: 'Verfügbarkeit der Quellen',
    sourcesAvailable: 'Quellen verfügbar',
    available: 'Verfügbar',
    unavailable: 'Nicht verfügbar',
    loadingLabel: 'Wird geladen',
    loadedExtract: 'Geladener Auszug',
    readAt: 'Gelesen am',
    glossary: 'Glossar'
  }
};

const valueLabels = {
  FR: {
    'Aide sociale': 'Aide sociale',
    'Aide Sociale': 'Aide sociale',
    'Don en nature': 'Don en nature',
    'Don matériel': 'Don matériel',
    'Stock à rapprocher': 'Stock à rapprocher',
    'Reclassé Social': 'Reclassé Social',
    'A rapprocher': 'À rapprocher',
    SOCIAL: 'Social',
    IMMO: 'Business',
    ADMIN_ORG: 'Gouvernance & Organisation',
    IMPORT_EXPORT: 'Business',
    TECH_DIGITAL: 'Digital',
    Team_ZH: 'Team ZH',
    Team_SN: 'Team SN',
    TZH: 'Team ZH',
    TSN: 'Team SN',
    Finances: 'Finances',
    Administration: 'Administration',
    Production: 'Production',
    'Stocks & Actifs': 'Stocks & Actifs',
    'Finance Social': 'Finance Social',
    Conception: 'Conception',
    'Mise en Place': 'Mise en Place',
    Consolidation: 'Consolidation',
    Dynamisation: 'Dynamisation'
  },
  EN: {
    'Aide sociale': 'Social aid',
    'Aide Sociale': 'Social aid',
    'Don en nature': 'In-kind donation',
    'Don matériel': 'Material donation',
    'Stock à rapprocher': 'Stock to reconcile',
    'Reclassé Social': 'Reclassified Social',
    'A rapprocher': 'To reconcile',
    SOCIAL: 'Social',
    IMMO: 'Business',
    ADMIN_ORG: 'Governance & Organization',
    IMPORT_EXPORT: 'Business',
    TECH_DIGITAL: 'Digital',
    Team_ZH: 'Team ZH',
    Team_SN: 'Team SN',
    TZH: 'Team ZH',
    TSN: 'Team SN',
    Finances: 'Finance',
    Administration: 'Administration',
    Production: 'Production',
    'Stocks & Actifs': 'Stock & Assets',
    'Finance Social': 'Finance Social',
    Conception: 'Design',
    'Mise en Place': 'Implementation',
    Consolidation: 'Consolidation',
    Dynamisation: 'Activation'
  },
  DE: {
    'Aide sociale': 'Sozialhilfe',
    'Aide Sociale': 'Sozialhilfe',
    'Don en nature': 'Sachspende',
    'Don matériel': 'Sachspende',
    'Stock à rapprocher': 'Lager abzugleichen',
    'Reclassé Social': 'Sozial umklassiert',
    'A rapprocher': 'Abzugleichen',
    SOCIAL: 'Soziales',
    IMMO: 'Business',
    ADMIN_ORG: 'Governance & Organisation',
    IMPORT_EXPORT: 'Business',
    TECH_DIGITAL: 'Digital',
    Team_ZH: 'Team ZH',
    Team_SN: 'Team SN',
    TZH: 'Team ZH',
    TSN: 'Team SN',
    Finances: 'Finanzen',
    Administration: 'Administration',
    Production: 'Produktion',
    'Stocks & Actifs': 'Bestand & Aktiven',
    'Finance Social': 'Finance Social',
    Conception: 'Konzeption',
    'Mise en Place': 'Umsetzung',
    Consolidation: 'Konsolidierung',
    Dynamisation: 'Aktivierung'
  }
};

const chartColors = ['#38bdf8', '#34d399', '#f59e0b', '#a78bfa', '#fb7185', '#22d3ee'];
const tabs = ['overview', 'architecture', 'processes', 'prospects', 'clients', 'ventes', 'dons', 'beneficiaires', 'assistant', 'resources', 'glossary'];

const designationTerms = {
  EN: [
    ['Soutien Ménage SN', 'Household support SN'],
    ['Soutien Menage SN', 'Household support SN'],
    ['Participation Cheikh', 'Cheikh contribution'],
    ['Aide sociale', 'Social aid'],
    ['Aide Sociale', 'Social aid'],
    ['Envoi Smartphones Parents SN', 'Smartphones sent to parents SN'],
    ['Soutien communautaire', 'Community support'],
    ['Soutien familial', 'Family support'],
    ['Don Matériel', 'Material donation'],
    ['Don Materiel', 'Material donation'],
    ['Préfinancement', 'Prefinancing'],
    ['Achat', 'Purchase'],
    ['Terrain', 'Land'],
    ['Article', 'Item'],
    ['Stock', 'Stock']
  ],
  DE: [
    ['Soutien Ménage SN', 'Haushaltsunterstützung SN'],
    ['Soutien Menage SN', 'Haushaltsunterstützung SN'],
    ['Participation Cheikh', 'Beitrag Cheikh'],
    ['Aide sociale', 'Sozialhilfe'],
    ['Aide Sociale', 'Sozialhilfe'],
    ['Envoi Smartphones Parents SN', 'Smartphones an Eltern SN gesendet'],
    ['Soutien communautaire', 'Gemeinschaftsunterstützung'],
    ['Soutien familial', 'Familienunterstützung'],
    ['Don Matériel', 'Sachspende'],
    ['Don Materiel', 'Sachspende'],
    ['Préfinancement', 'Vorfinanzierung'],
    ['Achat', 'Kauf'],
    ['Terrain', 'Grundstück'],
    ['Article', 'Artikel'],
    ['Stock', 'Lager']
  ]
};

const numberValue = (value) => Number(value) || 0;

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const cleanDateValue = (value) => {
  if (!value) return '';
  const raw = value?.value || value;
  if (typeof raw === 'string') return raw.slice(0, 10);
  try {
    return new Date(raw).toISOString().slice(0, 10);
  } catch {
    return String(raw).slice(0, 10);
  }
};

const isDonationCandidate = (item) => {
  const haystack = normalizeText([
    item.article,
    item.categorie,
    item.sous_categorie,
    item.fournisseur,
    item.localisation,
    item.bu,
    item.commentaires,
    item.statut
  ].join(' '));

  return (
    haystack.includes('don') ||
    haystack.includes('donateur') ||
    haystack.includes('donation') ||
    haystack.includes('nature') ||
    haystack.includes('social')
  );
};

const deriveBeneficiaryName = (row) => {
  const sourceName = String(row.beneficiaire || '').trim();
  return sourceName || 'Bénéficiaire à préciser';
};

const normalizeSocialRow = (row, index) => ({
  id: row.id || row.ref || `SOC-${index + 1}`,
  ref: row.ref || row.id || `SOC-${index + 1}`,
  date: cleanDateValue(row.date_created || row.date || row.date_operation),
  beneficiaire: deriveBeneficiaryName(row),
  designation: row.description || row.designation || '',
  typeAide: row.nature_sociale || row.category || 'Aide sociale',
  montantChf: numberValue(row.montant_chf ?? row.montantChf),
  montantCfa: numberValue(row.montant_cfa ?? row.montantCfa),
  tauxFx: numberValue(row.taux_fx),
  source: 'Finance Social',
  statut: 'Reclassé Social',
  agent: row.agent || '',
  team: row.team || '',
  departement: row.departement || 'Finances',
  pays: row.pays || 'SN',
  phaseProjet: row.phase_projet || '',
  commentaire: row.commentaire || ''
});

const normalizeInventoryDonation = (item, index) => ({
  id: item.source_id || item.id || item.ref || `DON-STOCK-${index + 1}`,
  ref: item.source_id || item.id || item.ref || `DON-STOCK-${index + 1}`,
  date: cleanDateValue(item.date_acquisition || item.date || item.created_at),
  donateur: item.fournisseur || 'À confirmer',
  nature: normalizeText(`${item.categorie} ${item.sous_categorie} ${item.commentaires}`).includes('don')
    ? 'Don en nature'
    : 'Stock à rapprocher',
  designation: item.article || item.name || '',
  montantChf: numberValue(item.valeur_chf ?? item.achat_chf ?? item.price),
  montantCfa: numberValue(item.valeur_cfa ?? item.achat_cfa),
  quantite: numberValue(item.quantite ?? item.quantity),
  unite: item.unite || '',
  destination: item.localisation || item.bu || '',
  bu: item.bu || '',
  source: 'Stocks & Actifs',
  statut: item.statut || item.status || 'A rapprocher',
  commentaire: item.commentaires || ''
});

const CRM = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const queryTab = new URLSearchParams(location.search).get('tab');
  const [activeTab, setActiveTab] = useState(tabs.includes(queryTab) ? queryTab : 'overview');
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [dons, setDons] = useState([]);
  const [sourceStatus, setSourceStatus] = useState({ social: 'loading', inventory: 'loading' });
  const [sourceTimestamps, setSourceTimestamps] = useState({ social: '', inventory: '' });
  const [selectedRecord, setSelectedRecord] = useState(null);

  const t = dictionaries[language] || dictionaries.FR;
  const tv = useCallback((value) => {
    if (isLegacyBuCode(value)) return translateDas(value, language);
    return valueLabels[language]?.[value] || valueLabels.FR[value] || value || '-';
  }, [language]);
  const translateDesignation = useCallback((value) => {
    if (!value || language === 'FR') return value;
    return (designationTerms[language] || []).reduce(
      (text, [source, target]) => text.replaceAll(source, target),
      String(value)
    );
  }, [language]);

  useEffect(() => {
    setActiveTab(tabs.includes(queryTab) ? queryTab : 'overview');
  }, [queryTab]);

  const selectTab = (tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    navigate(`/crm?${params.toString()}`);
  };

  useEffect(() => {
    let isMounted = true;

    const loadValidatedData = async () => {
      setSourceStatus({ social: 'loading', inventory: 'loading' });

      const [socialResult, inventoryResult] = await Promise.allSettled([
        api.getSocialFinance(300, 0),
        api.getInventory(500, 0)
      ]);

      if (!isMounted) return;

      const socialResponse = socialResult.status === 'fulfilled' ? socialResult.value : null;
      const inventoryResponse = inventoryResult.status === 'fulfilled' ? inventoryResult.value : null;
      const socialAvailable = Array.isArray(socialResponse?.data);
      const inventoryAvailable = Array.isArray(inventoryResponse?.data);
      const socialRows = socialAvailable ? socialResponse.data : [];
      const inventoryRows = inventoryAvailable ? inventoryResponse.data : [];

      setBeneficiaires(socialRows.map(normalizeSocialRow));
      setDons(inventoryRows.filter(isDonationCandidate).map(normalizeInventoryDonation));
      setSourceStatus({
        social: socialAvailable ? 'available' : 'unavailable',
        inventory: inventoryAvailable ? 'available' : 'unavailable'
      });
      setSourceTimestamps({
        social: socialAvailable ? socialResponse.timestamp || '' : '',
        inventory: inventoryAvailable ? inventoryResponse.timestamp || '' : ''
      });
    };

    loadValidatedData();
    return () => {
      isMounted = false;
    };
  }, []);

  const formatChf = (value) => `${numberValue(value).toLocaleString('fr-CH', { maximumFractionDigits: 2 })} CHF`;
  const formatCfa = (value) => `${Math.round(numberValue(value)).toLocaleString('fr-CH')} CFA`;
  const formatSourceCount = (status, value) => status === 'available' ? value : status === 'loading' ? '…' : '—';
  const formatSourceMoney = (status, value, formatter) => status === 'available' ? formatter(value) : status === 'loading' ? '…' : '—';
  const formatDate = (value) => {
    if (!value) return '-';
    try {
      return new Intl.DateTimeFormat(language === 'DE' ? 'de-DE' : language === 'EN' ? 'en-GB' : 'fr-FR').format(new Date(value));
    } catch {
      return value;
    }
  };

  const socialTotals = useMemo(() => beneficiaires.reduce((acc, item) => {
    acc.chf += item.montantChf;
    acc.cfa += item.montantCfa;
    return acc;
  }, { chf: 0, cfa: 0 }), [beneficiaires]);

  const donsTotals = useMemo(() => dons.reduce((acc, item) => {
    acc.chf += item.montantChf;
    acc.cfa += item.montantCfa;
    return acc;
  }, { chf: 0, cfa: 0 }), [dons]);

  const aidByType = useMemo(() => {
    const grouped = {};
    beneficiaires.forEach((item) => {
      const key = item.typeAide || 'Aide sociale';
      grouped[key] = (grouped[key] || 0) + 1;
    });
    return Object.entries(grouped).map(([name, value]) => ({ name: tv(name), value }));
  }, [beneficiaires, tv]);

  const donationsByNature = useMemo(() => {
    const grouped = {};
    dons.forEach((item) => {
      const key = item.nature || 'Don en nature';
      grouped[key] = (grouped[key] || 0) + item.montantChf;
    });
    return Object.entries(grouped).map(([name, value]) => ({ name: tv(name), value }));
  }, [dons, tv]);

  const socialByYear = useMemo(() => {
    const grouped = {};
    beneficiaires.forEach((item) => {
      const year = String(item.date || '').slice(0, 4);
      if (!/^\d{4}$/.test(year)) return;
      grouped[year] = (grouped[year] || 0) + item.montantChf;
    });
    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([year, value]) => ({ year, value }));
  }, [beneficiaires]);

  const Notice = ({ children }) => (
    <div className="mb-4 flex items-start gap-3 rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm text-blue-100">
      <Info size={18} className="mt-0.5 shrink-0 text-blue-300" />
      <span>{children}</span>
    </div>
  );

  const sourceStatusLabel = (status) => (
    status === 'available' ? t.available : status === 'loading' ? t.loadingLabel : t.unavailable
  );
  const sourceStatusClass = (status) => (
    status === 'available'
      ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200'
      : status === 'loading'
        ? 'border-blue-400/40 bg-blue-500/10 text-blue-200'
        : 'border-amber-400/40 bg-amber-500/10 text-amber-200'
  );
  const availableSourceCount = Object.values(sourceStatus).filter((status) => status === 'available').length;
  const sourcesLoading = Object.values(sourceStatus).some((status) => status === 'loading');

  const SourceStatusPanel = () => (
    <section className="mb-6 rounded-lg border border-slate-700 bg-slate-800 p-4" aria-label={t.sourceAvailability}>
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-100">{t.sourceAvailability}</h2>
          <p className="mt-1 text-sm leading-6 text-slate-300">{t.sourceScope}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'social', label: t.financeSocialSource },
            { key: 'inventory', label: t.inventorySource }
          ].map((source) => (
            <span key={source.key} className={`rounded-md border px-3 py-2 text-xs font-semibold ${sourceStatusClass(sourceStatus[source.key])}`}>
              {source.label}: {sourceStatusLabel(sourceStatus[source.key])}
              {sourceTimestamps[source.key] && ` · ${t.readAt} ${formatDate(sourceTimestamps[source.key])}`}
            </span>
          ))}
        </div>
      </div>
    </section>
  );

  const NextRegister = ({ title, purpose, fields, icon: Icon }) => (
    <section className="rounded-lg border border-slate-700 bg-slate-800 p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className="rounded-md border border-blue-400/30 bg-blue-500/10 p-2 text-blue-300">
            <Icon size={22} aria-hidden="true" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-bold text-white">{title}</h2>
              <span className="rounded border border-amber-400/40 bg-amber-500/10 px-2 py-1 text-xs font-semibold text-amber-200">
                {t.nextSubLot}
              </span>
              <span className="rounded border border-blue-400/40 bg-blue-500/10 px-2 py-1 text-xs font-semibold text-blue-200">
                {t.readOnlyPilot}
              </span>
            </div>
            <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-300">{purpose}</p>
            <p className="mt-1 max-w-4xl text-sm leading-6 text-slate-400">{t.noConsolidatedData}</p>
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {fields.map((field, index) => (
          <div key={field} className="flex min-h-20 items-center gap-3 rounded-md border border-slate-600 bg-slate-900/45 px-4 py-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-blue-400/40 bg-blue-500/10 text-sm font-bold text-blue-200">
              {index + 1}
            </span>
            <span className="text-sm font-semibold text-slate-100">{field}</span>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        <p className="rounded-md border border-slate-600 bg-slate-900/35 px-4 py-3 text-sm leading-6 text-slate-300">
          {t.commercialBoundary}
        </p>
        <p className="rounded-md border border-slate-600 bg-slate-900/35 px-4 py-3 text-sm leading-6 text-slate-300">
          {t.writesUnavailable}
        </p>
      </div>
    </section>
  );

  const DataTable = ({ title, type, rows, columns, renderRow, emptyText, summaryItems = [] }) => (
    <div>
      <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">{title}</h2>
          <p className="mt-1 text-sm text-slate-400">{t.readOnly}</p>
        </div>
        {summaryItems.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {summaryItems.map((item) => (
              <div key={item.label} className="rounded-lg border border-slate-700 bg-slate-800 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-slate-400">{item.label}</p>
                <p className="mt-1 text-lg font-bold text-white">{item.value}</p>
                {item.secondary && <p className="text-xs font-semibold text-slate-400">{item.secondary}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
      <TableControls
        rows={rows}
        maxHeight="34rem"
        renderEmpty={() => <div className="px-6 py-8 text-center text-slate-400">{emptyText}</div>}
        renderTable={(visibleRows) => (
          <table className="min-w-[1720px] text-sm">
            <thead className="sticky top-0 z-10 bg-slate-700">
              <tr>
                {columns.map((column) => (
                  <th key={column} className="px-4 py-3 text-left font-bold text-white">{column}</th>
                ))}
                <th className="px-4 py-3 text-left font-bold text-white">{t.actions}</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => setSelectedRecord({ type, title, item, mode: 'view' })}
                  className="cursor-pointer border-t border-slate-700 hover:bg-slate-700/50"
                >
                  {renderRow(item)}
                  <StandardActionsCell
                    item={item}
                    labels={{ view: t.view, edit: t.edit }}
                    onView={() => setSelectedRecord({ type, title, item, mode: 'view' })}
                    onEdit={() => setSelectedRecord({ type, title, item, mode: 'edit' })}
                  />
                </tr>
              ))}
            </tbody>
          </table>
        )}
      />
    </div>
  );

  const tableCell = (children, className = 'text-slate-300') => (
    <td className={`px-4 py-3 align-top ${className}`}>{children || '-'}</td>
  );
  const badge = (children) => (
    <span className="inline-flex rounded bg-slate-700 px-2 py-1 text-xs font-semibold text-slate-200">{children}</span>
  );

  const getRecordDetails = (record) => {
    if (!record) return [];
    const item = record.item;
    if (record.type === 'dons') {
      return [
        [t.ref, item.ref],
        [t.date, formatDate(item.date)],
        [t.donor, item.donateur],
        [t.designation, translateDesignation(item.designation)],
        [t.nature, tv(item.nature)],
        [t.amountChf, formatChf(item.montantChf)],
        [t.amountCfa, formatCfa(item.montantCfa)],
        [t.quantity, item.quantite],
        [t.unit, item.unite],
        [t.destination, item.destination],
        ['DAS', tv(item.bu)],
        [t.status, tv(item.statut)],
        [t.comment, item.commentaire],
        [t.source, tv(item.source)]
      ];
    }

    return [
      [t.ref, item.ref],
      [t.date, formatDate(item.date)],
      [t.beneficiary, item.beneficiaire],
      [t.designation, translateDesignation(item.designation)],
      [t.aidType, tv(item.typeAide)],
      [t.amountChf, formatChf(item.montantChf)],
      [t.amountCfa, formatCfa(item.montantCfa)],
      [t.fxRate, item.tauxFx ? item.tauxFx.toLocaleString('fr-CH', { maximumFractionDigits: 4 }) : '-'],
      [t.agent, item.agent],
      [t.team, tv(item.team)],
      [t.department, tv(item.departement)],
      [t.phase, tv(item.phaseProjet)],
      [t.country, item.pays],
      [t.comment, item.commentaire],
      [t.source, tv(item.source)]
    ];
  };

  return (
    <div className="m3s-business-module min-h-screen bg-slate-900 p-4 sm:p-8">
      <div className="mx-auto w-full max-w-[1800px]">
        <div className="min-w-0">
          <ModulePageTabs
            moduleId="commercial"
            language={language}
            activeTab={activeTab}
            onSelect={selectTab}
            tabs={[
              { tab: 'overview', label: t.overview },
              { tab: 'prospects', label: `${t.prospects} (0)` },
              { tab: 'clients', label: `${t.clients} (0)` },
              { tab: 'ventes', label: `${t.ventes} (0)` },
              { tab: 'dons', label: `${t.dons} (${dons.length})` },
              { tab: 'beneficiaires', label: `${t.beneficiaires} (${beneficiaires.length})` },
              { tab: 'glossary', label: t.glossary }
            ]}
          />
        </div>

        <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-5">
          <StandardKpiCard label={t.validatedSources} value={sourcesLoading ? '…' : `${availableSourceCount}/2`} secondary={t.sourcesAvailable} icon={Target} color="text-blue-400" />
          <StandardKpiCard label={t.socialFlows} value={formatSourceCount(sourceStatus.social, beneficiaires.length)} secondary={`${t.financeSocialSource} · ${t.loadedExtract}`} icon={HeartHandshake} color="text-cyan-400" />
          <StandardKpiCard label={t.totalSocial} value={formatSourceMoney(sourceStatus.social, socialTotals.chf, formatChf)} secondary={sourceStatus.social === 'available' ? `${formatCfa(socialTotals.cfa)} · ${t.loadedExtract}` : sourceStatusLabel(sourceStatus.social)} icon={TrendingUp} color="text-emerald-400" />
          <StandardKpiCard label={t.inKindDonations} value={formatSourceCount(sourceStatus.inventory, dons.length)} secondary={sourceStatus.inventory === 'available' ? `${formatChf(donsTotals.chf)} / ${formatCfa(donsTotals.cfa)} · ${t.loadedExtract}` : sourceStatusLabel(sourceStatus.inventory)} icon={Gift} color="text-purple-400" />
          <StandardKpiCard label={t.toBuild} value="3" secondary={`${t.prospects} / ${t.clients} / ${t.ventes}`} icon={Users} color="text-amber-400" />
        </div>

        <SourceStatusPanel />
        <Notice>{t.sourceNotice}</Notice>

        <ModuleTabContent activeTab={activeTab} ready={!sourcesLoading}>
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="rounded-lg border border-slate-700 bg-slate-800 p-6">
              <h3 className="mb-4 font-bold text-white">{t.socialByYear}</h3>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={socialByYear}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="year" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} formatter={(value) => formatChf(value)} />
                  <Line type="monotone" dataKey="value" stroke="#38bdf8" strokeWidth={2} dot={{ r: 4, fill: '#38bdf8' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <div className="rounded-lg border border-slate-700 bg-slate-800 p-6">
                <h3 className="mb-4 font-bold text-white">{t.beneficiariesByType}</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={aidByType} dataKey="value" nameKey="name" innerRadius={55} outerRadius={92} label={({ name, value }) => `${name}: ${value}`}>
                      {aidByType.map((entry, index) => <Cell key={entry.name} fill={chartColors[index % chartColors.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="rounded-lg border border-slate-700 bg-slate-800 p-6">
                <h3 className="mb-4 font-bold text-white">{t.donationsByNature}</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={donationsByNature}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="name" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#94a3b8" />
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} formatter={(value) => formatChf(value)} />
                    <Bar dataKey="value" fill="#a78bfa" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'architecture' && <FunctionArchitectureOverview moduleId="commercial" language={language} />}

        {activeTab === 'processes' && <FunctionProcessOverview moduleId="commercial" language={language} />}

        {activeTab === 'prospects' && (
          <NextRegister title={t.prospects} purpose={t.prospectsPurpose} fields={t.prospectsFields} icon={Target} />
        )}
        {activeTab === 'clients' && (
          <div id="crm-clients-register" className="scroll-mt-24" tabIndex="-1"><NextRegister title={t.clients} purpose={t.clientsPurpose} fields={t.clientsFields} icon={Users} /></div>
        )}
        {activeTab === 'ventes' && (
          <NextRegister title={t.ventes} purpose={t.salesPurpose} fields={t.salesFields} icon={TrendingUp} />
        )}

        {activeTab === 'dons' && (
          <div id="crm-donations-register" className="scroll-mt-24" tabIndex="-1"><DataTable
            title={t.dons}
            type="dons"
            rows={dons}
            emptyText={t.noDonationRows}
            summaryItems={[
              { label: t.inKindDonations, value: formatSourceCount(sourceStatus.inventory, dons.length), secondary: `${t.inventorySource} · ${t.loadedExtract}` },
              { label: t.amountChf, value: formatSourceMoney(sourceStatus.inventory, donsTotals.chf, formatChf) },
              { label: t.amountCfa, value: formatSourceMoney(sourceStatus.inventory, donsTotals.cfa, formatCfa) }
            ]}
            columns={[t.ref, t.date, t.donor, t.designation, t.nature, t.amountChf, t.amountCfa, t.quantity, t.unit, t.destination, 'DAS', t.status, t.comment, t.source]}
            renderRow={(item) => (
              <>
                {tableCell(item.ref, 'font-semibold text-slate-200')}
                {tableCell(formatDate(item.date))}
                {tableCell(item.donateur)}
                {tableCell(<strong>{translateDesignation(item.designation)}</strong>, 'text-slate-200')}
                {tableCell(tv(item.nature))}
                {tableCell(formatChf(item.montantChf), 'font-bold text-emerald-300')}
                {tableCell(formatCfa(item.montantCfa), 'font-semibold text-amber-300')}
                {tableCell(item.quantite)}
                {tableCell(item.unite)}
                {tableCell(item.destination)}
                {tableCell(badge(tv(item.bu)))}
                {tableCell(badge(tv(item.statut)))}
                {tableCell(item.commentaire, 'text-slate-400')}
                {tableCell(tv(item.source))}
              </>
            )}
          /></div>
        )}

        {activeTab === 'beneficiaires' && (
          <div id="crm-beneficiaries-register" className="scroll-mt-24" tabIndex="-1"><DataTable
            title={t.beneficiaires}
            type="beneficiaires"
            rows={beneficiaires}
            emptyText={t.noBeneficiaryRows}
            summaryItems={[
              { label: t.socialFlows, value: formatSourceCount(sourceStatus.social, beneficiaires.length), secondary: `${t.financeSocialSource} · ${t.loadedExtract}` },
              { label: t.amountChf, value: formatSourceMoney(sourceStatus.social, socialTotals.chf, formatChf) },
              { label: t.amountCfa, value: formatSourceMoney(sourceStatus.social, socialTotals.cfa, formatCfa) }
            ]}
            columns={[t.ref, t.date, t.beneficiary, t.designation, t.aidType, t.amountChf, t.amountCfa, t.fxRate, t.agent, t.team, t.department, t.phase, t.country, t.comment, t.source]}
            renderRow={(item) => (
              <>
                {tableCell(item.ref, 'font-semibold text-slate-200')}
                {tableCell(formatDate(item.date))}
                {tableCell(<strong>{item.beneficiaire}</strong>, 'text-slate-200')}
                {tableCell(translateDesignation(item.designation), 'text-slate-400')}
                {tableCell(tv(item.typeAide))}
                {tableCell(formatChf(item.montantChf), 'font-bold text-emerald-300')}
                {tableCell(formatCfa(item.montantCfa), 'font-semibold text-amber-300')}
                {tableCell(item.tauxFx ? item.tauxFx.toLocaleString('fr-CH', { maximumFractionDigits: 4 }) : '-')}
                {tableCell(item.agent)}
                {tableCell(tv(item.team))}
                {tableCell(tv(item.departement))}
                {tableCell(tv(item.phaseProjet))}
                {tableCell(badge(item.pays))}
                {tableCell(item.commentaire, 'text-slate-400')}
                {tableCell(tv(item.source))}
              </>
            )}
          /></div>
        )}

        {activeTab === 'resources' && <FunctionResourcesOverview moduleId="commercial" language={language} onSelectTab={selectTab} />}

        {activeTab === 'assistant' && <FunctionAssistant moduleId="commercial" language={language} />}

        {activeTab === 'glossary' && <CRMGlossary language={language} />}

        <ChildTabPlaceholder moduleId="commercial" language={language} activeTab={activeTab} handledTabs={tabs} />
        </ModuleTabContent>
      </div>
      <StandardRecordSheetModal
        open={Boolean(selectedRecord)}
        title={selectedRecord?.title || ''}
        eyebrow={selectedRecord?.mode === 'edit' ? t.edit : t.recordSheet}
        description={selectedRecord?.mode === 'edit' ? t.editPending : ''}
        details={getRecordDetails(selectedRecord)}
        closeLabel={t.close}
        onClose={() => setSelectedRecord(null)}
      />
    </div>
  );
};

export default CRM;
